import { useState, useEffect, useRef } from "react";
import { PANEL, TEAL, CREAM, CREAM60, CREAM30, BORDER, AMBER, GREEN, RED, NAVY } from "../utils/constants";
import { scoreOccupation, exposureLabel } from "../utils/exposure";
import { extractSkills } from "../utils/skillExtractor";
import { loadSkills, loadOccupations } from "../lib/taxonomy";
import PulseLoader from "./PulseLoader";
import Delta from "./Delta";
import { compareAssessments, mostRecentPrior } from "../utils/delta";
import { recordSignal, deltaEvent, EVENTS } from "../lib/signals";

const statusColour = (s) => (s === "automated" ? RED : s === "augmented" ? AMBER : GREEN);
const scoreColour = (n) => (n >= 7 ? RED : n >= 4 ? AMBER : GREEN);

function Axis({ label, value, hint }) {
  return (
    <div style={{ flex: 1, minWidth: 120 }}>
      <div style={{ fontSize: 10, color: CREAM30, textTransform: "uppercase", letterSpacing: 1,
                    fontFamily: "'JetBrains Mono', monospace", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: CREAM, fontFamily: "'JetBrains Mono', monospace" }}>
        {value}
      </div>
      <div style={{ fontSize: 11, color: CREAM60, lineHeight: 1.4, marginTop: 2 }}>{hint}</div>
    </div>
  );
}

export default function Exposure({ parsed, occupationCode, tasks, onExplore, onScored, onBrowse, history }) {
  const [skills, setSkills] = useState(null);
  const [score, setScore] = useState(null);
  const [occupation, setOccupation] = useState(null);

  useEffect(() => {
    let dead = false;
    (async () => {
      const [dict, occs] = await Promise.all([loadSkills(), loadOccupations()]);
      // The confirmed activities are what make this score THIS person's rather
      // than the occupation's average (ADR-037). Scoring without them threw the
      // refinement away at the last step — the ranking improved on screen and
      // the number underneath stayed generic.
      const s = scoreOccupation(tasks || [], { confirmed: parsed?.confirmedActivities || [] });
      const sk = extractSkills(parsed.taskText, parsed.skills, dict);
      if (!dead) {
        setScore(s); setSkills(sk); setOccupation({ occ: occs.get(occupationCode), dict });
        onScored?.(s);
      }
    })();
    return () => { dead = true; };
  }, [occupationCode, tasks, parsed]);

  // The read side of the append-only history (ADR-022). `mostRecentPrior` skips
  // this session's own just-written record, which would otherwise compare the
  // score against itself and always report "no change".
  //
  // Computed BEFORE the loading guard: `compareAssessments` returns null for a
  // missing score, and the effect below is a hook, so it cannot sit behind an
  // early return.
  const verdict = compareAssessments(mostRecentPrior(history), score, {
    occupationCode,
    occupationTitle: occupation?.occ?.title || null,
  });

  // Telemetry from an effect, never from render: this component re-renders on
  // every state change, and a signal recorded inline would count once per
  // render rather than once per assessment (ADR-038).
  const signalled = useRef(null);
  useEffect(() => {
    if (!verdict) return;
    const key = `${occupationCode}:${verdict.overall.from}:${verdict.overall.to}`;
    if (signalled.current === key) return;
    signalled.current = key;
    // Two facts, deliberately separate: that they came back at all (the
    // retention question), and which way it moved (the product question).
    recordSignal(EVENTS.ASSESSMENT_REPEAT, { occupationCode });
    recordSignal(deltaEvent(verdict), { occupationCode });
  }, [verdict, occupationCode]);

  if (!score || !skills) return <PulseLoader text="Reading the work, task by task…" />;

  const transferable = skills.matched.filter((s) => s.category === "transferable").slice(0, 8);
  const knowledge = skills.matched.filter((s) => s.category === "knowledge").slice(0, 6);

  // A LinkedIn export with no role descriptions leaves almost nothing to match
  // against, and "These travel with you" over a single chip reads as a broken
  // product rather than a thin profile. So we show what the ROLE typically
  // carries alongside what the history evidenced — in a separate group, under a
  // separate claim. Never merged: one is what this person demonstrably did, the
  // other is what people in this occupation usually do. Collapsing that
  // distinction is exactly the flattery this product exists not to sell.
  const evidenced = new Set(transferable.map((s) => s.id));
  const typical = (() => {
    const occ = occupation?.occ;
    if (!occ?.skills) return [];
    const byId = new Map((occupation.dict || []).map((d) => [d.id, d]));
    return Object.entries(occ.skills)
      .map(([id, importance]) => ({ ...(byId.get(id) || {}), id, importance }))
      .filter((s) => s.category === "transferable" && !evidenced.has(s.id) && s.importance >= 3.5)
      .sort((a, b) => b.importance - a.importance)
      .slice(0, Math.max(0, 8 - transferable.length));
  })();
  const typicalInferred = Boolean(occupation?.occ?.skillsInferred);

  return (
    <div style={{ animation: "fadeUp 0.4s ease" }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ color: CREAM, fontSize: 22, margin: "0 0 4px", fontFamily: "'Space Grotesk', sans-serif" }}>
          Where AI meets your work
        </h2>
        <p style={{ color: CREAM60, fontSize: 14, margin: 0 }}>
          Scored task by task, not role by role. Every number below is built from the rows beneath it.
        </p>
      </div>

      <div style={{ background: PANEL, border: `1px solid ${BORDER}`,
                    borderTop: `2px solid ${scoreColour(score.exposure)}`, borderRadius: 12,
                    padding: 20, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                      flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 12, color: TEAL, fontWeight: 600, textTransform: "uppercase",
                          letterSpacing: 1, fontFamily: "'JetBrains Mono', monospace" }}>Overall exposure</div>
            <div style={{ fontSize: 13, color: CREAM60, marginTop: 4 }}>
              across {score.tasks.length} tasks in this role
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 36, fontWeight: 700, color: scoreColour(score.exposure),
                          fontFamily: "'JetBrains Mono', monospace", lineHeight: 1 }}>{score.exposure}</div>
            <div style={{ fontSize: 12, color: scoreColour(score.exposure), fontWeight: 600 }}>
              {exposureLabel(score.exposure)}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <Axis label="Digital" value={score.digitality} hint="Can it be done through a screen?" />
          <Axis label="Judgment" value={score.judgment} hint="Must someone own the outcome?" />
          <Axis label="Coordination" value={score.coordination} hint="Does it need context across people?" />
          <Axis label="Premium" value={score.premium} hint="Will anyone pay extra for it?" />
        </div>
      </div>

      <Delta verdict={verdict} />

      {score.premiumWarnings > 0 && (
        <div style={{ background: `${AMBER}10`, border: `1px solid ${AMBER}40`, borderRadius: 12,
                      padding: 18, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: AMBER, marginBottom: 6 }}>
            The price is moving faster than the task
          </div>
          <p style={{ color: CREAM60, fontSize: 13, lineHeight: 1.6, margin: 0 }}>
            {score.premiumWarnings === 1
              ? "One of your tasks is something AI can't fully do"
              : `${score.premiumWarnings} of your tasks are things AI can't fully do`} — but that
            people have stopped paying extra for. That's the earlier signal, and the one most
            exposure tools miss: the work survives, the premium doesn't.
          </p>
        </div>
      )}

      <div style={{ fontSize: 12, color: TEAL, fontWeight: 600, marginBottom: 10, textTransform: "uppercase",
                    letterSpacing: 1, fontFamily: "'JetBrains Mono', monospace" }}>Task by task</div>
      <div style={{ display: "grid", gap: 8, marginBottom: 20 }}>
        {score.tasks.slice(0, 10).map((t, i) => (
          <div key={i} style={{ background: `${statusColour(t.status)}08`,
                                border: `1px solid ${statusColour(t.status)}30`, borderRadius: 8, padding: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 6 }}>
              <span style={{ color: CREAM, fontSize: 13, lineHeight: 1.5 }}>{t.task}</span>
              <span style={{ fontSize: 15, fontWeight: 700, color: statusColour(t.status),
                             fontFamily: "'JetBrains Mono', monospace" }}>{t.exposure}</span>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 3,
                             background: `${statusColour(t.status)}20`, color: statusColour(t.status),
                             fontWeight: 600, textTransform: "uppercase" }}>{t.status}</span>
              {t.premiumWarning && (
                <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 3,
                               background: `${AMBER}20`, color: AMBER, fontWeight: 600 }}>PREMIUM GONE</span>
              )}
              <span style={{ fontSize: 10, color: CREAM30, fontFamily: "'JetBrains Mono', monospace" }}>
                dig {t.digitality} · jud {t.judgment} · coo {t.coordination} · prem {t.premium}
              </span>
            </div>
          </div>
        ))}
      </div>

      {(transferable.length > 0 || typical.length > 0) && (
        <>
          <div style={{ fontSize: 12, color: GREEN, fontWeight: 600, marginBottom: 4, textTransform: "uppercase",
                        letterSpacing: 1, fontFamily: "'JetBrains Mono', monospace" }}>Your transferable skills</div>
          <p style={{ color: CREAM30, fontSize: 12, margin: "0 0 10px" }}>
            These travel with you. They're what a new bundle gets built from.
          </p>

          {transferable.length > 0 && (
            <>
              <div style={{ fontSize: 10, color: CREAM60, marginBottom: 6, letterSpacing: 1,
                            fontFamily: "'JetBrains Mono', monospace" }}>EVIDENCED IN YOUR HISTORY</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                {transferable.map((s) => (
                  <span key={s.id} title={s.evidence ? `matched on "${s.evidence}"` : undefined}
                        style={{ background: `${GREEN}12`, border: `1px solid ${GREEN}40`,
                                 borderRadius: 8, padding: "8px 14px", fontSize: 13, color: CREAM }}>
                    {s.name}
                  </span>
                ))}
              </div>
            </>
          )}

          {typical.length > 0 && (
            <>
              <div style={{ fontSize: 10, color: CREAM60, marginBottom: 6, letterSpacing: 1,
                            fontFamily: "'JetBrains Mono', monospace" }}>
                TYPICAL OF THIS ROLE — NOT YET EVIDENCED
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                {typical.map((s) => (
                  <span key={s.id} style={{ background: "transparent", border: `1px dashed ${CREAM30}`,
                                            borderRadius: 8, padding: "8px 14px", fontSize: 13, color: CREAM60 }}>
                    {s.name}
                  </span>
                ))}
              </div>
              <p style={{ color: CREAM30, fontSize: 11, margin: "0 0 20px", lineHeight: 1.5 }}>
                Drawn from the occupation, not from your profile
                {typicalInferred ? ", and itself inferred from closely related occupations O*NET has rated" : ""}.
                Add detail to your profile and these move up — or drop off.
              </p>
            </>
          )}
        </>
      )}

      {knowledge.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, color: CREAM30, marginBottom: 8,
                        fontFamily: "'JetBrains Mono', monospace" }}>DOMAIN KNOWLEDGE DETECTED</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {knowledge.map((s) => (
              <span key={s.id} style={{ background: `${CREAM}08`, border: `1px solid ${CREAM30}`,
                                        borderRadius: 6, padding: "5px 10px", fontSize: 12, color: CREAM60 }}>
                {s.name}
              </span>
            ))}
          </div>
        </div>
      )}

      <div style={{ background: PANEL, border: `1px solid ${BORDER}`, borderRadius: 10, padding: 14,
                    marginBottom: 16, fontSize: 11, color: CREAM30, lineHeight: 1.6,
                    fontFamily: "'JetBrains Mono', monospace" }}>
        Method: {score.method} · task data from the O*NET 30.3 Database (US Dept of Labor),
        used under CC BY 4.0 with modifications. Scores are ours, not O*NET's.
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button onClick={onExplore}
          style={{ flex: 1, minWidth: 220, background: `linear-gradient(135deg, ${TEAL}, #00A3A9)`,
                   color: NAVY, border: "none", padding: "14px 28px", borderRadius: 8,
                   cursor: "pointer", fontSize: 15, fontWeight: 700 }}>
          Where these skills can take you →
        </button>
        <button onClick={onBrowse}
          style={{ background: "none", border: `1px solid ${CREAM30}`, color: CREAM60,
                   padding: "14px 20px", borderRadius: 8, cursor: "pointer", fontSize: 13 }}>
          Browse all roles
        </button>
      </div>
    </div>
  );
}
